/* Copyright (c) 2020-present CloudIO Inc */

import { emptyFunction } from '@cloudio-saas/statex';
import { isEmpty } from 'utils/isEmpty';
import { Platform } from '../../../../platform/Platform';
import {
  ComponentManager,
  ComponentManagerFn,
  RequiredItem,
  ViewAttributeChooserProp,
} from '../../../../registry/Component.type';
import { ItemType } from '../../../../registry/ComponentRegistry';
import { baseComponentManager } from '../../../../registry/Components';
import {
  baseColumnProps,
  formatFunction,
  isBaseColumnPropertyVisible,
} from '../BaseColumn';
import { defaultValue } from './LookupSelectColumn';

const LookupSelectColumnManager: ComponentManagerFn<'LookupSelectColumn'> = (
  platform: Platform,
): ComponentManager<'LookupSelectColumn'> => {
  let callback: () => void = emptyFunction;
  return {
    ...baseComponentManager<'LookupSelectColumn'>(
      platform,
      'LookupSelectColumn',
    ),
    type: 'LookupSelectColumn',
    name: 'Lookup Select Column',
    isChildAllowed: (page, item, childType) =>
      (
        [
          'ColumnFooter',
          'TriggerOnChange',
          'TriggerOnClick',
          'TriggerOnCellClick',
          'LookupReturnValueMapping',
          'TriggerAfterServerValidate',
        ] as ItemType[]
      ).includes(childType),
    defaultValue,
    props: {
      lookup: {
        label: 'Lookup',
        type: 'LookupCodeChooser',
      },
      ...baseColumnProps,
      viewAttribute: {
        ...(baseColumnProps.viewAttribute as ViewAttributeChooserProp),
        getAttributeDataTypes: (item: RequiredItem<'LookupSelectColumn'>) => {
          return !isEmpty(item.props.lookup.code)
            ? item.props.lookup.isNumeric
              ? ['Integer', 'Decimal', 'Double']
              : ['String', 'YN', 'TF']
            : ['String', 'YN', 'TF', 'Integer', 'Decimal', 'Double'];
        },
        attributeRefreshCallback: (cb: () => void) => {
          callback = cb;
        },
      },
      placeholder: {
        label: 'Placeholder',
        type: 'string',
      },
      serverValidateOnChange: {
        category: 'Validation',
        label: 'Server Validate On Change',
        type: 'boolean',
      },
      formatFunction,
    },
    onPropertyChange: (property, value) => {
      if (property === 'lookup') {
        callback();
      }
      return {
        [property]: value,
      };
    },
    isPropertyVisible(prop, itemData, { appUid, itemId, pageId }): boolean {
      if (
        itemId &&
        !isBaseColumnPropertyVisible({ appUid, platform, itemId, pageId, prop })
      ) {
        return false;
      }
      return true;
    },
    generateShortId: (item) =>
      typeof item.label === 'string' ? item.label : item.label?.value,
    getDisplayText: (item) =>
      typeof item.props.label === 'string'
        ? item.props.label
        : item.props.label.value ?? '',
    category: 'Data Grid',
    icon: { iconName: 'io-select', iconType: 'cloudio' },
    replaceableWith: [
      'SelectColumn',
      'AsyncSelectColumn',
      'StringColumn',
      'CheckboxColumn',
    ],
    description: `**Overview**<br/>
The Lookup Select Column is a dropdown input component specifically designed for System Lookups (also known as Reference Data or Code Tables).
Unlike the standard Select Column (which queries a dynamic Data Store), this component binds to a centralized, pre-defined "Lookup Code" managed at the application level. It is the standard way to handle common, reusable lists like Countries, Currencies, Time Zones, or Status Codes across your entire application.

**1. Configuration & Binding**<br/>
To set up this column, you map a system lookup to a field in your Data Grid.
* **Lookup:** Select the specific System Lookup code (e.g., "LOV_COUNTRY" or "LOV_CURRENCY") that defines the list of options.
* **View Attribute:** Select the attribute in your Data Store where the value will be saved.
* **Smart Typing:** The component automatically filters available attributes based on the Lookup type.
    * If the Lookup is Numeric (e.g., IDs 1, 2, 3), it allows binding to Integer, Decimal, or Double.
    * If the Lookup is Character-based (e.g., "US", "UK"), it allows binding to String, YN, or TF.

**2. Advanced Data Mapping**<br/>
System Lookups often contain more than just a Code and a Description (e.g., a "Country" lookup might also have "ISO Code," "Dial Code," and "Region"). You can copy these extra details into other columns when a user makes a selection.
* **Child Component (Lookup Return Value Mapping):** Purpose: Map a field from the selected Lookup record to a different column in the same grid row.
* **Example:** User selects "United States". The system automatically populates the "Dial Code" column with "+1" and the "Currency" column with "USD".

**3. Validation & Interaction**<br/>
* **Server Validate On Change:**
    * **Enabled:** Triggers a backend validation check immediately upon selection. Useful for conditional logic (e.g., "You cannot select 'Shipped' status if 'Tracking Number' is empty").
* **Triggers:** Supports standard events like **TriggerOnChange** to execute client-side logic or calculations when the value is modified.

**4. Comparison: Which Select Column to use?**<br/>
* **Lookup Select Column:** Source is System Lookup (Static/Managed). Best for Global reference lists (Country, Gender, Status).
* **Select Column:** Source is Data Store (Dynamic). Best for Transactional lists (Active Projects, Departments).
* **Async Select Column:** Source is Server Query (Large Data). Best for Massive datasets (> 1,000 items) needing search-as-you-type.`,
  };
};

export default LookupSelectColumnManager;
